import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { authApi } from '../services/api'
import { BACKEND_URL, IS_NATIVE_SHELL } from '../services/config'
import { prewarmVoiceAudio } from '../components/voice/audioContext'
import { BrandWordmark } from '../components/brand/BrandMark'
import { LogIn, Eye, EyeOff, Loader2, ArrowLeft, Download } from 'lucide-react'

// Stable, version-agnostic installer links. The server resolves the
// current build from the electron-updater manifest and 302-redirects to
// the real file (see apps/server/src/routes/download.ts), so these hrefs
// never need touching on a new release.
const dl = (os: 'mac' | 'win', arch?: 'arm64' | 'x64') =>
  `${BACKEND_URL}/api/download/desktop?os=${os}${arch ? `&arch=${arch}` : ''}`

// Google Identity Services is loaded lazily by injecting the script
// the first time Login mounts. The accounts.id API is the official
// "Sign in with Google" SDK that hands us an ID token in the callback.
// https://developers.google.com/identity/gsi/web/reference/js-reference
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string
            callback: (response: { credential: string }) => void
          }) => void
          renderButton: (
            parent: HTMLElement,
            options: {
              type?: 'standard' | 'icon'
              theme?: 'outline' | 'filled_blue' | 'filled_black'
              size?: 'large' | 'medium' | 'small'
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
              shape?: 'rectangular' | 'pill' | 'circle' | 'square'
              logo_alignment?: 'left' | 'center'
              width?: number
            },
          ) => void
        }
      }
    }
  }
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  // Inline "forgot password" mode: the same card swaps the login form
  // for a single email field + Send button, then a neutral confirmation.
  const [mode, setMode] = useState<'login' | 'forgot'>('login')
  const [forgotSubmitting, setForgotSubmitting] = useState(false)
  const [forgotSent, setForgotSent] = useState(false)
  const login = useAuthStore((s) => s.login)
  const login2fa = useAuthStore((s) => s.login2fa)
  const loginWithGoogle = useAuthStore((s) => s.loginWithGoogle)
  const signupExchange = useAuthStore((s) => s.signupExchange)
  // ViewSX self-service signup: '#signup=<code>' lands here straight off the
  // landing's success page (spec §7 "Owner entry exchange"). Exchanging is
  // usually sub-second, but the form is hidden while it's in flight so the
  // visitor never sees a flash of an empty login form they don't need.
  const [exchanging, setExchanging] = useState(false)
  // OSPROD-180: when a 2FA account passes the password, we hold the
  // challenge here and swap the form for the SMS-code step.
  const [twoFa, setTwoFa] = useState<{ challengeId: string; phoneHint: string } | null>(null)
  const [code, setCode] = useState('')
  const [trustDevice, setTrustDevice] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Bare login → land on the smart Business-OS picker at "/" (see
  // RootLanding in App.tsx). The old default `/channels/@me` dumped
  // every fresh login into the DM list, which broke for users who
  // expect the workspace's server channels first.
  const redirect = searchParams.get('redirect') || '/'
  // Set by the force-logout path (socket `auth:force-logout` + the 401
  // `account_disabled` interceptor) so a kicked user sees WHY they're
  // back at sign-in instead of a bare form.
  const wasDisabled = searchParams.get('disabled') === '1'
  const googleBtnRef = useRef<HTMLDivElement>(null)

  // Detect the visitor's OS so we lead with the matching installer and
  // demote the others to secondary links. Best-effort UA sniff — falls
  // back to showing both platforms equally when it can't tell.
  const platform = useMemo<'mac' | 'win' | 'other'>(() => {
    const ua = (navigator.userAgent || '').toLowerCase()
    if (ua.includes('mac')) return 'mac'
    if (ua.includes('win')) return 'win'
    return 'other'
  }, [])

  // ViewSX self-service signup exchange (spec §7 + §12). Runs once on mount:
  // a fragment (never sent to the server on page load) is the only safe
  // place a one-time code can travel through a redirect. Cleared from the
  // URL immediately via history.replaceState — win or lose, it must never
  // sit in the address bar/history, and a refresh must not re-submit it.
  useEffect(() => {
    const hash = window.location.hash
    if (!hash.startsWith('#signup=')) return
    const code = decodeURIComponent(hash.slice('#signup='.length))
    window.history.replaceState(null, '', window.location.pathname + window.location.search)
    if (!code) return

    setError('')
    setExchanging(true)
    void (async () => {
      try {
        const { serverId } = await signupExchange(code)
        navigate(`/servers/${serverId}`)
      } catch {
        // Neutral message — mirrors the anti-enumeration posture of the rest
        // of the signup flow (no "expired" vs "already used" vs "invalid"
        // distinction leaked to the browser).
        setError('Sign-in link expired — log in with your email and password.')
        setExchanging(false)
      }
    })()
  }, [navigate, signupExchange])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    // Warm the shared playback AudioContext NOW, before any await, so the
    // submit's transient user activation is still in scope and the context
    // comes up 'running' rather than 'suspended'. Best-effort and mic-free
    // (see prewarmVoiceAudio) — harmless if the login below then fails.
    prewarmVoiceAudio()
    try {
      const result = await login(email, password)
      // OSPROD-180: a 2FA account needs the SMS code first — show that step
      // and DON'T navigate yet.
      if (result.status === 'awaiting_2fa') {
        setCode('')
        setTrustDevice(false)
        setTwoFa({ challengeId: result.challengeId, phoneHint: result.phoneHint })
        return
      }
      // Denoiser pre-warm (RNNoise WASM/worklet + resident DFN worker)
      // is fired centrally by the auth effect in App.tsx — it runs for
      // BOTH this fresh login and a returning token-restored session, so
      // we don't duplicate it here. prewarmVoiceAudio above stays because
      // it must run synchronously under THIS submit gesture to bring the
      // playback context up 'running' rather than 'suspended'.
      navigate(redirect)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed')
    }
  }

  // OSPROD-180: submit the SMS code that completes a 2FA login.
  const handle2faSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!twoFa) return
    setVerifying(true)
    try {
      await login2fa(twoFa.challengeId, code, trustDevice)
      navigate(redirect)
    } catch (err: any) {
      const ec = err?.response?.data?.error
      if (ec === 'wrong_code') setError(`Incorrect code.${typeof err?.response?.data?.attemptsLeft === 'number' ? ` ${err.response.data.attemptsLeft} attempts left.` : ''}`)
      else if (ec === 'code_expired') setError('That code expired. Please sign in again.')
      else if (ec === 'too_many_attempts') setError('Too many attempts. Please sign in again.')
      else setError(err.response?.data?.error || 'Verification failed. Try again.')
    } finally {
      setVerifying(false)
    }
  }

  // Forgot-password request. We deliberately show the SAME neutral
  // confirmation whether or not the email maps to an account — the
  // server never reveals existence, and neither should the UI. The
  // request resolves 200 even for unknown emails, so we only land in
  // catch on a network/5xx error.
  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setForgotSubmitting(true)
    try {
      await authApi.forgotPassword(email)
      setForgotSent(true)
    } catch {
      // Don't surface backend specifics — keep the same neutral outcome
      // even if the request itself errored, to avoid probing differences.
      setForgotSent(true)
    } finally {
      setForgotSubmitting(false)
    }
  }

  const showForgot = () => {
    setError('')
    setForgotSent(false)
    setMode('forgot')
  }

  const backToLogin = () => {
    setError('')
    setForgotSent(false)
    setMode('login')
  }

  // Inject the GIS script on first mount, then init + render the
  // official Google button into our slot. Guarded against re-renders +
  // missing client id (we just skip rendering the button in that case).
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleBtnRef.current) return
    let cancelled = false

    function init() {
      if (cancelled || !window.google || !googleBtnRef.current) return
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID!,
        callback: async ({ credential }) => {
          if (!credential) return
          setError('')
          prewarmVoiceAudio()
          try {
            await loginWithGoogle(credential)
            navigate(redirect)
          } catch (err: any) {
            setError(err.response?.data?.error || 'Google login failed')
          }
        },
      })
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: 320,
      })
    }

    if (window.google?.accounts?.id) {
      init()
      return () => { cancelled = true }
    }
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://accounts.google.com/gsi/client"]',
    )
    if (existing) {
      existing.addEventListener('load', init, { once: true })
      return () => { cancelled = true; existing.removeEventListener('load', init) }
    }
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = init
    document.head.appendChild(script)
    return () => { cancelled = true }
  }, [loginWithGoogle, navigate, redirect])

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-xl p-8 shadow-lg">
        {/* White label §4.1: the sign-in surface is the first thing a
            customer's employee sees, so it carries the tenant brand — the
            uploaded logo when there is one, the brand name otherwise. */}
        <BrandWordmark className="mb-6" />

        {wasDisabled && mode === 'login' && (
          <div className="mb-4 rounded-lg border border-border bg-muted px-3 py-2.5 text-sm text-destructive">
            Your account has been disabled. Contact your administrator if you think this is a mistake.
          </div>
        )}

        {exchanging ? (
          <div className="py-10 text-center">
            <Loader2 size={24} className="animate-spin mx-auto mb-3 text-primary" />
            <p className="text-sm text-muted-foreground">Signing you in…</p>
          </div>
        ) : twoFa ? (
          <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-foreground">Two-factor verification</h1>
              <p className="text-muted-foreground text-sm mt-1">
                Enter the code we texted to <span className="text-foreground font-medium">{twoFa.phoneHint}</span>.
              </p>
            </div>
            <form onSubmit={handle2faSubmit} className="space-y-4">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="123456"
                autoFocus
                className="w-full bg-card text-foreground rounded px-3 py-3 text-center text-lg tracking-[0.5em] border border-border focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
                <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} className="w-4 h-4 accent-primary rounded" />
                Trust this device for 30 days
              </label>
              {error && <p className="text-destructive text-sm">{error}</p>}
              <button
                type="submit"
                disabled={verifying || code.length < 4}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2.5 rounded transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {verifying && <Loader2 size={16} className="animate-spin" />}
                Verify and sign in
              </button>
              <button
                type="button"
                onClick={() => { setTwoFa(null); setError(''); setCode('') }}
                className="w-full inline-flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition"
              >
                <ArrowLeft size={16} />
                Back to login
              </button>
            </form>
          </>
        ) : mode === 'forgot' ? (
          <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-foreground">Reset your password</h1>
              <p className="text-muted-foreground text-sm mt-1">
                Enter your account email and we'll send you a reset link.
              </p>
            </div>

            {forgotSent ? (
              <div>
                <div className="rounded-lg border border-border bg-muted px-3 py-2.5 text-sm text-foreground">
                  If an account exists for that email, we've sent a password reset link.
                </div>
                <button
                  type="button"
                  onClick={backToLogin}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 text-sm text-primary hover:underline"
                >
                  <ArrowLeft size={16} />
                  Back to login
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                    className="w-full bg-card text-foreground rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary border border-border"
                    required
                  />
                </div>
                {error && <p className="text-destructive text-sm">{error}</p>}
                <button
                  type="submit"
                  disabled={forgotSubmitting}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2.5 rounded transition flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {forgotSubmitting && <Loader2 size={16} className="animate-spin" />}
                  Send reset link
                </button>
                <button
                  type="button"
                  onClick={backToLogin}
                  className="w-full inline-flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition"
                >
                  <ArrowLeft size={16} />
                  Back to login
                </button>
              </form>
            )}
          </>
        ) : (
          <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-foreground">Welcome back!</h1>
              <p className="text-muted-foreground text-sm mt-1">We're so excited to see you again!</p>
            </div>

            {GOOGLE_CLIENT_ID && (
              <>
                <div ref={googleBtnRef} className="flex justify-center mb-4" />
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center" aria-hidden>
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-[11px] uppercase tracking-wider">
                    <span className="bg-card px-2 text-muted-foreground">or with email</span>
                  </div>
                </div>
              </>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-card text-foreground rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary border border-border"
                  required
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-bold text-muted-foreground uppercase">Password</label>
                  <button
                    type="button"
                    onClick={showForgot}
                    className="text-xs text-primary hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-card text-foreground rounded px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary border border-border"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              {error && <p className="text-destructive text-sm">{error}</p>}
              <button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2.5 rounded transition flex items-center justify-center gap-2"
              >
                <LogIn size={18} />
                Log In
              </button>
            </form>
            {/* Registration is invitation-only — there is no public sign-up
                link. Invited users reach /register via their emailed invite link. */}

            {/* Desktop-app download links — browser only. Hidden inside any
                native shell (packaged Electron, Capacitor mobile) — there's
                nothing to download there, and on a phone they're noise. */}
            {!IS_NATIVE_SHELL && (
            <div className="mt-6 pt-4 border-t border-border">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground text-center mb-3">
                Get the desktop app
              </p>

              {/* Mac ships a single universal (Intel+ARM) dmg — one button,
                  runs on every Mac. Lead with the visitor's likely OS. */}
              {platform === 'win' && (
                <div className="space-y-2">
                  <a
                    href={dl('win')}
                    className="w-full inline-flex items-center justify-center gap-2 bg-muted hover:bg-muted/70 text-foreground text-sm font-medium py-2.5 rounded border border-border transition"
                  >
                    <Download size={16} />
                    Download for Windows
                  </a>
                  {/* OSPROD-252: secondary-OS link rendered as bare text while the
                      primary was a full button — visually inconsistent. Both OS
                      downloads now share the same button treatment. */}
                  <a
                    href={dl('mac')}
                    className="w-full inline-flex items-center justify-center gap-2 bg-muted hover:bg-muted/70 text-foreground text-sm font-medium py-2.5 rounded border border-border transition"
                  >
                    <Download size={16} />
                    Download for Mac
                  </a>
                </div>
              )}

              {platform === 'mac' && (
                <div className="space-y-2">
                  <a
                    href={dl('mac')}
                    className="w-full inline-flex items-center justify-center gap-2 bg-muted hover:bg-muted/70 text-foreground text-sm font-medium py-2.5 rounded border border-border transition"
                  >
                    <Download size={16} />
                    Download for Mac
                  </a>
                  {/* OSPROD-252: same button treatment for the secondary OS. */}
                  <a
                    href={dl('win')}
                    className="w-full inline-flex items-center justify-center gap-2 bg-muted hover:bg-muted/70 text-foreground text-sm font-medium py-2.5 rounded border border-border transition"
                  >
                    <Download size={16} />
                    Download for Windows
                  </a>
                </div>
              )}

              {platform === 'other' && (
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href={dl('mac')}
                    className="inline-flex items-center justify-center gap-2 bg-muted hover:bg-muted/70 text-foreground text-sm font-medium py-2.5 rounded border border-border transition"
                  >
                    <Download size={16} />
                    Mac
                  </a>
                  <a
                    href={dl('win')}
                    className="inline-flex items-center justify-center gap-2 bg-muted hover:bg-muted/70 text-foreground text-sm font-medium py-2.5 rounded border border-border transition"
                  >
                    <Download size={16} />
                    Windows
                  </a>
                </div>
              )}
            </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
